import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../parts/modal-new";
import DateInput from "../../../../../parts/inputs/date-input";
import { toDisplayWeight, toKg } from "./viewTokens";

const g = (t, key, opts) => t(`nutritionPage.progress.goal.${key}`, opts);

// Single source of truth for what this modal prefills to — used both to
// seed the weight input on open and as the dirty-check baseline below.
const resolvePrefillWeight = (goalData, isImperial) => toDisplayWeight(goalData.targetWeight, isImperial);

// Bodyweight goal "Adjust" modal — same submit-form + full-navigation flow as
// GoalEmptyForm.js's own create form, hitting the EXISTING /client-goals/
// edit-goal action (already used by Profile's AddGoal.js) rather than a
// no-reload fetch. A dedicated JSON action was tried first, but the empty
// state already accepts a full reload on create — the user's call was to
// keep create/edit/clear consistent with that instead of introducing a
// second persistence pattern for one card. Clear goal confirms client-side
// here (like TdeeAdjustModal.js's own Reset) rather than relying on
// delete-client-goal's own isomorphic confirm() — that confirm can't be
// awaited from React, so the modal couldn't know whether to stay open on
// cancel; delete-client-goal-confirmed (client-goals.js) is the same delete
// minus that confirm, for exactly this caller.
export const GoalAdjustModal = ({ isOpen, onClose, goalData, athlete, units }) => {
  const { t } = useTranslation(["athlete-portal", "coaching-portal"]);
  const isImperial = units === "imperial";
  const weightUnit = isImperial ? "lb" : "kg";
  const athleteId = String(athlete?._id || "");

  const [weight, setWeight] = useState("");
  const [date, setDate] = useState(() => new Date());

  // Reset every time the modal opens — prefilled from the current goal.
  useEffect(() => {
    if (!isOpen) return;
    setWeight(String(resolvePrefillWeight(goalData, isImperial)));
    setDate(moment(String(goalData.utcTargetDate), "YYYYMMDD").toDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const parsedWeight = Number.parseFloat(weight);
  // DateInput's onChange doesn't hand back a Date instance — it's rome's own
  // "data" event payload, a plain string (date-input.js's rome.on("data", ...)
  // handler) — so `date` is a Date only until the coach touches the picker,
  // then a string. moment(...) accepts both, same as GoalEmptyForm.js's own
  // handleSubmit already relies on for this exact component.
  const isValidDate = moment(date).isValid();
  // Computed once here and reused below (both for the dirty-check and for
  // handleSave's own body) instead of re-running moment().format() twice.
  const currentUtcDate = isValidDate ? Number(moment(date).format("YYYYMMDD")) : null;
  const isValid = !Number.isNaN(parsedWeight) && parsedWeight > 0 && isValidDate;
  // Save stays disabled until something actually differs from the goal this
  // modal opened with — same prefill expression as the useEffect above,
  // recomputed each render instead of stored, since goalData/isImperial
  // don't change while the modal is open.
  const originalDisplayWeight = resolvePrefillWeight(goalData, isImperial);
  const isWeightChanged = parsedWeight !== originalDisplayWeight;
  const isDateChanged = isValidDate && currentUtcDate !== goalData.utcTargetDate;
  const canSave = isValid && (isWeightChanged || isDateChanged);

  // Handles the save action
  const handleSave = () => {
    if (!canSave) return;
    submit(`/actions/client-goals/edit-goal/${goalData._id}?proxyAthlete=${athleteId}`, {
      method: "POST",
      body: {
        type: "bodyWeight",
        details: { targetWeight: toKg(parsedWeight, isImperial) },
        utcTargetDate: currentUtcDate,
        targetDate: moment(date).format("YYYY-MM-DD"),
      },
    });
    // submit-form fires the navigation asynchronously (a queued form click,
    // not a promise) — close right away instead of waiting on it, same as
    // GoalEmptyForm.js's create flow never needed to "wait" for its own
    // submit. Rill's refresh swaps in the saved value moments later.
    onClose();
  };

  // Handles the clear action
  const handleClear = () => {
    // Confirmed here, synchronously, same as TdeeAdjustModal.js's own
    // handleReset — cancel leaves the modal open, nothing submitted.
    if (!window.confirm(t("coaching-portal:clientGoalsActions.confirmDeleteGoal"))) return;
    // proxyAthlete goes in the body, not the URL — a GET-method form submit
    // (submit-form) replaces the action URL's query string with its own
    // serialized fields on send, so a query param hardcoded into the URL
    // here would get silently dropped (same pattern as DayExerciseOptions.js/
    // MonthDay.js's own exercise-delete GET submits).
    submit(`/actions/client-goals/delete-client-goal-confirmed/${goalData._id}`, {
      method: "GET",
      body: { proxyAthlete: athleteId },
    });
    onClose();
  };

  return (
    <Modal
      title={g(t, "adjustModal.title")}
      className="np-goal-modal"
      onClose={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="body np-goal-modal__body">
        <div className="np-goal-modal__fields">
          <div>
            <span className="np-goal__field-label np-goal__field-label--empty">
              {g(t, "targetWeightLabel", { unit: weightUnit })}
            </span>
            <input
              type="number"
              step="0.1"
              min="0"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="np-goal__input"
            />
          </div>
          <div>
            <span className="np-goal__field-label np-goal__field-label--empty">
              {g(t, "byDate")}
            </span>
            <DateInput
              name="goal-adjust-target-date"
              value={date}
              onChange={(d) => setDate(d)}
              customClassNames="np-goal__input"
            />
          </div>
        </div>

        <button
          type="button"
          data-button-type="secondary"
          className="np-hero__adjust-btn np-goal-modal__clear-btn"
          onClick={handleClear}
        >
          <span className="np-plan-bar__cta-label">{g(t, "adjustModal.clearGoal")}</span>
        </button>
      </div>

      <footer className="np-goal-modal__footer">
        <button
          type="button"
          data-button-type="secondary"
          className="np-modal-footer__cancel-btn"
          onClick={onClose}
        >
          {g(t, "adjustModal.cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          className="np-modal-footer__save-btn"
          onClick={handleSave}
          disabled={!canSave}
        >
          {g(t, "adjustModal.save")}
        </button>
      </footer>
    </Modal>
  );
};

GoalAdjustModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  goalData: PropTypes.object.isRequired,
  athlete: PropTypes.object,
  units: PropTypes.string,
};

export default GoalAdjustModal;
